import { Clock3, ListMusic, Pause, Play, Repeat2, RotateCcw, Search, Square, Trash2 } from 'lucide-solid';
import { createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { BusyButton, CardSection, ConfirmModal, EmptyState, ScreenHeader } from '../components/ui';
import { formatClock, formatScoreVersion, humanScoreName } from '../lib/format';
import { canDeleteScore } from '../lib/scoreDeletion';
import type { ScoreSummary } from '../lib/types';
import { useVenue } from '../state/VenueProvider';
import { useSmoothPlayhead } from '../state/useSmoothPlayhead';
import { TEST_IDS } from '../testIds';

export function ScoresScreen(): JSX.Element {
  const venue = useVenue();
  const transport = venue.transport;
  const currentScore = createMemo(() => venue.scores().find((score) => score.scoreId === transport()?.scoreId));
  const currentName = createMemo(() => humanScoreName(transport()?.scoreId, currentScore()?.name || currentScore()?.title || transport()?.scoreName));
  const duration = createMemo(() => transport()?.durationSeconds ?? currentScore()?.durationSeconds ?? currentScore()?.latestVersion?.durationSeconds ?? 0);
  /**
   * A score is NOW PLAYING only while the room says it is running — playing, or paused part-way through
   * it. Loading a score is not playing it: the daemon keeps a finished or stopped score STAGED and goes
   * on reporting its `scoreId` with `state: 'stopped'` (the same fact the score rows rely on below), so
   * a card gated on "a score is loaded" stands over a silent room, with a transport an operator did not
   * ask for, naming something they are not listening to. Nothing stands in its place — the room is not
   * playing anything, and the list underneath is the whole answer.
   */
  const nowPlaying = createMemo(() => Boolean(transport()?.scoreId) && ['playing', 'paused'].includes(transport()?.state ?? ''));
  const playhead = useSmoothPlayhead(duration);
  const [pendingDelete, setPendingDelete] = createSignal<ScoreSummary>();
  const [query, setQuery] = createSignal('');
  const filteredScores = createMemo(() => {
    const value = query().trim().toLocaleLowerCase();
    return value ? venue.scores().filter((score) => scoreName(score).toLocaleLowerCase().includes(value)) : venue.scores();
  });

  function scoreName(score: ScoreSummary): string {
    return humanScoreName(score.scoreId, score.name || score.title);
  }

  function scoreVersion(score: ScoreSummary): string {
    return score.versionId || score.scoreVersionId || score.latestVersionId || '';
  }

  function deletionAllowed(score: ScoreSummary): boolean {
    const owned = venue.cloudScores().some((candidate) => candidate.scoreId === score.scoreId);
    return canDeleteScore(venue.role(), Boolean(venue.user()), owned);
  }

  function scoreVersionLabel(score: ScoreSummary): string {
    const number = score.versionNumber ?? score.latestVersion?.versionNumber;
    const finalizedAt = score.finalizedAt ?? score.latestVersion?.finalizedAt;
    return formatScoreVersion(number, finalizedAt);
  }

  function assignmentFor(score: ScoreSummary) {
    const rigId = score.rigId ?? venue.selectedRigId();
    return venue.scoreAssignments().find((assignment) => assignment.scoreId === score.scoreId
      && (!assignment.rigId || assignment.rigId === rigId));
  }

  function addressSourceFor(score: ScoreSummary): 'score' | 'rig' {
    const sources = assignmentFor(score)?.oscAddressSources ?? {};
    const conflicts = score.oscAddressConflicts ?? [];
    return conflicts.length > 0 && conflicts.every(({ scoreFixtureId }) => sources[scoreFixtureId] === 'rig')
      ? 'rig' : 'score';
  }

  async function setAddressSource(score: ScoreSummary, source: 'score' | 'rig'): Promise<boolean> {
    const assignment = assignmentFor(score);
    const venueId = venue.selectedVenue()?.venueId;
    const roomId = venue.selectedRoom()?.roomId;
    const rigId = assignment?.rigId ?? score.rigId ?? venue.selectedRigId();
    if (!assignment || !venueId || !roomId || !rigId) return false;
    const oscAddressSources = { ...assignment.oscAddressSources };
    for (const { scoreFixtureId } of score.oscAddressConflicts ?? []) {
      oscAddressSources[scoreFixtureId] = source;
    }
    const result = await venue.runAction(`score-osc-${score.scoreId}`, () => venue.cloudCall('setScoreOscAddressSources', {
      venueId, roomId, rigId, scoreId: score.scoreId, oscAddressSources,
    }));
    return result !== undefined;
  }

  /**
   * Pressing play on the score that is ALREADY playing does nothing — it never restarts it. The daemon
   * agrees (its transport ignores `play` while playing), and "Start over" is the explicit control for
   * going back to the top. Pressing it on the playing score's row pauses, which is what the icon says.
   */
  async function playScore(score: ScoreSummary): Promise<void> {
    const isCurrent = transport()?.scoreId === score.scoreId;
    if (isCurrent && transport()?.state === 'playing') {
      await venue.runAction('score-play-toggle', () => venue.controllerCall('transport', { action: 'pause' }));
      return;
    }
    if (!await venue.confirmTakeover({
      source: 'score',
      action: `plays "${scoreName(score)}"`,
    })) return;
    await venue.runAction(`score-${score.scoreId}`, async () => {
      const requestedVersion = scoreVersion(score);
      const alreadyLoaded = isCurrent && (!requestedVersion || transport()?.versionId === requestedVersion);
      if (!alreadyLoaded) {
        await venue.controllerCall('load_score', { scoreId: score.scoreId, versionId: requestedVersion });
      }
      await venue.controllerCall('transport', { action: 'play' });
    }, `Playing ${scoreName(score)}`);
  }

  async function toggleCurrentScore(): Promise<void> {
    if (transport()?.state === 'playing') {
      await venue.runAction('score-play-toggle', () => venue.controllerCall('transport', { action: 'pause' }));
      return;
    }
    if (!await venue.confirmTakeover({
      source: 'score',
      action: `plays "${currentName()}"`,
    })) return;
    await venue.runAction('score-play-toggle', () => venue.controllerCall('transport', { action: 'play' }));
  }

  /**
   * Where on the score the OPERATOR's finger is, while it is down. `undefined` at every other moment,
   * which is the whole of the rule below: the bar is the room's, except for the instant a person is
   * moving it.
   */
  const [scrub, setScrub] = createSignal<number>();

  /** Is the bar something an operator may move right now? A score with no length cannot be scrubbed. */
  const scrubbable = (): boolean => venue.controlsEnabled() && duration() > 0;

  /**
   * What the bar draws: the room's position, and the operator's finger only while it is down.
   *
   * On release the bar goes straight back to the room — it does NOT sit at the released value waiting
   * to be proved right. A control left showing a value the room has not confirmed is this console's own
   * recorded failure (B44): the hold outlives the gesture, and the page goes on describing an edit that
   * nothing ever applied. So the seek is a request, the room's own snapshot is the answer, and the only
   * thing said in between is that a seek is in the air (`aria-busy`). A finger on the bar is different
   * in kind: it is not an un-landed edit, it is a person in the middle of a movement, and a bar that
   * refused to follow it could not be used to find a place in a score at all.
   */
  const shownSeconds = (): number => scrub() ?? playhead.position();

  /** Where on the score a pointer at this x lands, in seconds. Pixels are the screen's business. */
  function secondsAt(track: HTMLElement, clientX: number): number {
    const box = track.getBoundingClientRect();
    if (box.width <= 0) return 0;
    const ratio = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
    return ratio * duration();
  }

  function seekTo(positionSeconds: number): void {
    void venue.runAction('score-seek', () => venue.controllerCall('transport', {
      action: 'seek',
      positionSeconds,
    }));
  }

  function beginScrub(event: PointerEvent & { currentTarget: HTMLDivElement }): void {
    if (!scrubbable() || event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setScrub(secondsAt(event.currentTarget, event.clientX));
  }

  function moveScrub(event: PointerEvent & { currentTarget: HTMLDivElement }): void {
    if (scrub() === undefined) return;
    setScrub(secondsAt(event.currentTarget, event.clientX));
  }

  /**
   * A press with no movement is a seek to the point pressed, which is what a person expects of a
   * progress bar, so there is no separate click handler and no minimum drag.
   */
  function endScrub(event: PointerEvent & { currentTarget: HTMLDivElement }): void {
    const target = scrub();
    if (target === undefined) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setScrub(undefined);
    seekTo(secondsAt(event.currentTarget, event.clientX) || target);
  }

  /** The pointer was taken away mid-gesture. Nothing was asked for, so nothing is sent. */
  function cancelScrub(event: PointerEvent & { currentTarget: HTMLDivElement }): void {
    if (scrub() === undefined) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setScrub(undefined);
  }

  /** Five seconds a press, and the two ends — the same bar, for somebody who is not using a pointer. */
  function scrubByKey(event: KeyboardEvent): void {
    if (!scrubbable()) return;
    const from = playhead.position();
    const step = 5;
    const target = event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? from - step
      : event.key === 'ArrowRight' || event.key === 'ArrowUp' ? from + step
        : event.key === 'Home' ? 0
          : event.key === 'End' ? duration()
            : undefined;
    if (target === undefined) return;
    event.preventDefault();
    seekTo(Math.min(duration(), Math.max(0, target)));
  }

  async function deleteScore(score: ScoreSummary): Promise<void> {
    const name = scoreName(score);
    const result = await venue.runAction(
      `delete-score-${score.scoreId}`,
      () => venue.deleteScore(score.scoreId),
      `Deleted ${name}`,
    );
    if (result) setPendingDelete(undefined);
  }

  return (
    <>
      <ScreenHeader title="Scores" description="Start a score, follow its progress, and keep the room looping when needed." />
      <Show when={nowPlaying()}>
        <section class="now-playing surface" data-testid={TEST_IDS.nowPlaying}>
          <div class="now-playing-art"><ListMusic size={26} /></div>
          <div class="now-playing-copy">
            <span>Now playing</span>
            <h2 data-testid={TEST_IDS.nowPlayingName}>{currentName()}</h2>
            <div class="transport-progress">
              <div
                class="progress-track"
                classList={{ scrubbing: scrub() !== undefined }}
                role="slider"
                tabindex={scrubbable() ? 0 : -1}
                aria-label="Playhead"
                aria-valuemin={0}
                aria-valuemax={Math.round(duration())}
                aria-valuenow={Math.round(shownSeconds())}
                aria-valuetext={`${formatClock(shownSeconds())} of ${formatClock(duration())}`}
                aria-disabled={scrubbable() ? undefined : 'true'}
                /* Nothing is DRAWN for a seek in the air — the fill returning to the room's position is
                   the answer, and that answer is visual only, so `aria-busy` says the same thing to
                   somebody who cannot see it. It is not a second rendering of a state on screen. */
                aria-busy={venue.actionBusy() === 'score-seek' ? 'true' : undefined}
                data-testid={TEST_IDS.playheadScrubber}
                onPointerDown={beginScrub}
                onPointerMove={moveScrub}
                onPointerUp={endScrub}
                onPointerCancel={cancelScrub}
                onKeyDown={scrubByKey}
              ><span style={{ width: `${duration() > 0 ? Math.min(100, Math.max(0, shownSeconds() / duration() * 100)) : 0}%` }} /></div>
              <small>{formatClock(scrub() ?? playhead.clockSeconds())} / {formatClock(duration())}</small>
            </div>
          </div>
          <Show when={!venue.rigUnassigned()}>
            <div class="transport-controls">
              <BusyButton busyKey="score-restart" class="icon-button transport-button" aria-label="Start over" disabled={!venue.controlsEnabled()} onClick={() => void venue.runAction('score-restart', () => venue.controllerCall('transport', { action: 'seek', positionSeconds: 0 }))}><RotateCcw size={19} /></BusyButton>
              <BusyButton busyKey="score-play-toggle" class="icon-button transport-button main" aria-label={transport()?.state === 'playing' ? 'Pause score' : 'Play score'} disabled={!venue.controlsEnabled()} onClick={() => void toggleCurrentScore()} data-testid={TEST_IDS.transportToggle} data-transport-state={transport()?.state ?? 'unknown'}>
                {transport()?.state === 'playing' ? <Pause size={22} /> : <Play size={22} />}
              </BusyButton>
              <BusyButton busyKey="score-stop" class="icon-button transport-button" aria-label="Stop score" disabled={!venue.controlsEnabled()} onClick={() => void venue.runAction('score-stop', () => venue.controllerCall('transport', { action: 'stop' }), 'Playback stopped')} data-testid={TEST_IDS.transportStop}><Square size={18} /></BusyButton>
              <BusyButton busyKey="score-loop" class="loop-button" classList={{ active: transport()?.loop === true }} disabled={!venue.controlsEnabled()} onClick={() => void venue.runAction('score-loop', () => venue.controllerCall('transport', { action: 'set_loop', loop: !transport()?.loop }))}><Repeat2 size={17} /> Loop</BusyButton>
            </div>
          </Show>
        </section>
      </Show>

      <CardSection
        class="score-library"
        bodyClass="score-list"
        bodyTestId={TEST_IDS.scoreList}
        heading={(
          <div class="section-heading">
            <div><h2>Available scores</h2></div>
          </div>
        )}
        tools={(
          <label class="library-search"><Search size={17} aria-hidden="true" /><input type="search" value={query()} onInput={(event) => setQuery(event.currentTarget.value)} placeholder="Search scores" aria-label="Search scores" /></label>
        )}
        footer={(
          <Show when={venue.scores().length > 0 && !filteredScores().length}>
            <p class="quiet-empty card-section-note">No scores match “{query().trim()}”.</p>
          </Show>
        )}
      >
        <Show when={venue.scores().length > 0} fallback={<EmptyState icon={<ListMusic size={24} />} title="No scores assigned" detail="A score will appear here when it is assigned to this room." />}>
            <For each={filteredScores()}>
              {(score) => {
                const current = () => transport()?.scoreId === score.scoreId;
                /** This row's score is the loaded one AND the daemon reports it as running. */
                const playing = () => current() && transport()?.state === 'playing';
                const length = () => score.durationSeconds ?? score.latestVersion?.durationSeconds ?? 0;
                return (
                  <article class="score-row" classList={{ current: current() }} data-score-id={score.scoreId}>
                    <Show when={!venue.rigUnassigned()}>
                      <BusyButton
                        busyKey={`score-${score.scoreId}`}
                        class="score-play"
                        aria-label={`Play ${scoreName(score)}`}
                        disabled={!venue.controlsEnabled() || !scoreVersion(score)}
                        onClick={() => void playScore(score)}
                        data-testid={TEST_IDS.scorePlay}
                      >
                        {playing() ? <Pause size={19} /> : <Play size={19} />}
                      </BusyButton>
                    </Show>
                    <div class="score-title">
                      <strong>{scoreName(score)}</strong>
                      <span data-testid={TEST_IDS.scoreVersion}><Clock3 size={14} /> {formatClock(length())} · {scoreVersionLabel(score)}</span>
                      <Show when={(score.oscAddressConflicts?.length ?? 0) > 0}>
                        <span class="score-osc-conflicts" id={`score-osc-conflicts-${score.scoreId}`} role="status">{score.oscAddressConflicts?.map(
                          ({ scoreAddress, rigAddress }) => `${scoreAddress} ≠ ${rigAddress}`,
                        ).join(' · ')}</span>
                        <Show when={assignmentFor(score) && ['owner', 'manager', 'staff'].includes(venue.role())}>
                          <select
                            class="score-osc-source"
                            aria-describedby={`score-osc-conflicts-${score.scoreId}`}
                            value={addressSourceFor(score)}
                            disabled={venue.actionBusy() === `score-osc-${score.scoreId}`}
                            onChange={(event) => {
                              const control = event.currentTarget;
                              void setAddressSource(score, control.value as 'score' | 'rig')
                                .then((saved) => { if (!saved && control.isConnected) control.value = addressSourceFor(score); });
                            }}
                          >
                            <option value="score">Score addresses</option>
                            <option value="rig">Rig addresses</option>
                          </select>
                        </Show>
                      </Show>
                    </div>
                    {/*
                      The bars mark the score the daemon currently holds, and they MOVE only while it is
                      actually playing. Stopping does not unload the score (the daemon keeps it staged and
                      reports `stopped`), so a stopped row is still the current one — it just has to sit
                      still. Both facts come from the daemon's transport, never from what was last clicked.
                    */}
                    <div class="score-row-actions">
                      <Show when={current()}>
                        <span
                          class="playing-bars"
                          classList={{ playing: playing() }}
                          aria-label={playing() ? 'Playing now' : 'Loaded, not playing'}
                          data-testid={TEST_IDS.scoreBars}
                          data-playing={playing() ? 'true' : 'false'}
                        ><i /><i /><i /></span>
                      </Show>
                      <Show when={deletionAllowed(score)}>
                        <button
                          class="icon-button danger-hover score-delete"
                          aria-label={`Delete ${scoreName(score)}`}
                          disabled={venue.actionBusy() === `delete-score-${score.scoreId}`}
                          onClick={() => setPendingDelete(score)}
                          data-testid={TEST_IDS.scoreDelete}
                        ><Trash2 size={17} /></button>
                      </Show>
                    </div>
                  </article>
                );
              }}
            </For>
        </Show>
      </CardSection>

      <ConfirmModal
        open={Boolean(pendingDelete())}
        title={`Delete '${pendingDelete() ? scoreName(pendingDelete()!) : ''}'?`}
        detail="It is removed from every room and controller."
        confirmLabel="Delete score"
        tone="danger"
        busyKey={`delete-score-${pendingDelete()?.scoreId ?? ''}`}
        onConfirm={() => void deleteScore(pendingDelete()!)}
        onCancel={() => setPendingDelete(undefined)}
      />
    </>
  );
}
